import React, { useEffect, useRef, useState } from 'react'
import { Button, Card, CardContent, Divider, Grid, TextField } from '@mui/material'
import { ArrowRight, SearchOutlined } from '@mui/icons-material'
import { Formik } from 'formik'
import { LabelField, GreenText } from 'components'
import * as controllers from 'controllers'
import { useLayout } from 'context'
import { useNavigate, useSearchParams } from 'react-router-dom'
import AddDamageStepper from './AddDamageStepper'
import { DamageDto, damageValidationSchema } from 'dtos/damageDto'
import { VehicleDamageDto } from 'dtos'

interface Step1ScanOrSearchProps {
  // needsClusterConversionInHouseDetails: VehicleNeedsClusterConversionInHouseDetailsDto
  onBack: () => void
  onNext: () => void
  damage: VehicleDamageDto
  vehicleGuid?: string
  setDamageData: (data: VehicleDamageDto) => void
  loading: boolean
}

export default function Step1ScanOrSearch({ onBack, onNext, damage, vehicleGuid, setDamageData }: Step1ScanOrSearchProps) {
  const layout = useLayout()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const autoSearchRanRef = useRef(false)
  const [search, setSearch] = useState<string>('')
  const [searchError, setSearchError] = useState<string>('')
  const [hasSearched, setHasSearched] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [initialValues, setInitialValues] = useState<VehicleDamageDto>(new VehicleDamageDto())

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value)
  }

  const searchVehicle = (setValues: any, searchText?: string, options?: { skipNavigate?: boolean }) => {
    const term = (searchText ?? search).trim()
    if (!term) {
      setSearchError('Please enter the last 6 of the VIN or Importer Stock #')
      return
    }

    layout.setIsLayoutLoading(true)
    controllers
      .getDamageSearchOrScanVehicle(term)
      .then(response => {
        if (response.value) {
          setValues({
            ...response.value
          })
          setDamageData(response.value)
          setInitialValues(response.value)
          setHasSearched(true)
          setSearch('')
          setSearchError('')

          const matchGuid = response.value.vehicleGuid
          if (!options?.skipNavigate && matchGuid && matchGuid !== vehicleGuid) {
            navigate(`/damage/add-damage/${matchGuid}`)
          }
        } else {
          setValues(new DamageDto())
          setHasSearched(false)
          setSearchError('No Match Found')
        }
      })
      .finally(() => {
        layout.setIsLayoutLoading(false)
      })
  }

  useEffect(() => {
    if (autoSearchRanRef.current) {
      return
    }
    const auto = searchParams.get('autoSearch')
    const prefill = searchParams.get('prefillSearch')?.trim()
    if (auto !== '1' || !prefill || !vehicleGuid) {
      return
    }
    autoSearchRanRef.current = true
    layout.setIsLayoutLoading(true)
    controllers
      .getDamageSearchOrScanVehicle(prefill)
      .then(response => {
        if (response.value?.vehicleGuid === vehicleGuid) {
          setDamageData(response.value)
          setInitialValues(response.value)
          setHasSearched(true)
          setSearchError('')
          navigate(`/damage/add-damage/${vehicleGuid}`, { replace: true })
        } else if (response.value?.vehicleGuid) {
          setDamageData(response.value)
          setInitialValues(response.value)
          setHasSearched(true)
          setSearchError('')
          navigate(`/damage/add-damage/${response.value.vehicleGuid}`, { replace: true })
        } else {
          setHasSearched(false)
          setSearchError('No Match Found')
        }
      })
      .finally(() => {
        layout.setIsLayoutLoading(false)
      })
  }, [vehicleGuid, searchParams, layout, navigate, setDamageData])

  // const handleStartClusterConversion = async () => {
  //   if (!vehicleGuid) {
  //     console.error('Vehicle GUID is missing')
  //     return
  //   }

  //   setLoading(true)

  //   try {
  //     await controllers.updateNeedsClusterConversionInHouseDetails(vehicleGuid, needsClusterConversionInHouseDetails)
  //     navigate('/to-do-list')
  //   } catch (error) {
  //     console.error('Failed tp start cluster conversion:', error)
  //   } finally {
  //     setLoading(false)
  //   }
  // }

  const handleNext = () => {
    onNext()
  }

  return (
    <Formik
      enableReinitialize={true}
      initialValues={damage}
      validate={values => {}}
      onSubmit={(values, formikHelpers) => {
        // handleStartClusterConversion()
        // formikHelpers.setSubmitting(false)
      }}
      validateOnBlur={false}
      validateOnChange={false}
      validationSchema={damageValidationSchema}
    >
      {({
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        isSubmitting,
        setFieldValue,
        setValues,
        touched,
        setTouched,
        values,
        validateForm
      }) => (
        <form onSubmit={handleSubmit}>
          <Card>
            <CardContent>
              <Grid container spacing={1} alignItems='center'>
                <Grid item xs={12}>
                  <AddDamageStepper activeStep={0} />
                </Grid>

                <Grid item xs={12}>
                  <Divider />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    autoFocus
                    error={Boolean(searchError)}
                    helperText={searchError}
                    fullWidth
                    label='Last 6 of VIN or Importer Stock #'
                    onChange={handleSearchChange}
                    value={search}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        searchVehicle(setValues, undefined)
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Button
                    endIcon={<SearchOutlined />}
                    color='primary'
                    fullWidth
                    onClick={() => searchVehicle(setValues, undefined)}
                    variant='contained'
                  >
                    SEARCH
                  </Button>
                </Grid>

                {hasSearched && damage && (
                  <>
                    <Grid item xs={12}>
                      <LabelField label='VIN' value={initialValues?.vin} />
                    </Grid>

                    <Grid item xs={12}>
                      <LabelField label='Year' value={initialValues?.year} />
                    </Grid>

                    <Grid item xs={12}>
                      <LabelField label='Make' value={initialValues?.make} />
                    </Grid>

                    <Grid item xs={12}>
                      <LabelField label='Model' value={initialValues?.model} />
                    </Grid>
                  </>
                )}

                <Grid item xs={12} marginTop={1}>
                  <Button
                    color='primary'
                    fullWidth
                    endIcon={<ArrowRight />}
                    onClick={handleNext}
                    variant='contained'
                    disabled={!hasSearched || !values || loading}
                  >
                    NEXT
                  </Button>
                </Grid>

                <Grid item xs={12} marginTop={1}>
                  <GreenText variant='outlined'>
                    {`**Vehicle Damage Steps**
                    
Displays the current step of the vehicle in the Damage 
process.                 
                    
**Last 6 of VIN/Importer Stock Number Search:**

A free-text input field for entering either the last six 
characters of the VIN or the Importer Stock Number of a 
vehicle.

**SEARCH Button:**

Clicking the Search button performs a check to match the 
entered Last 6 of VIN or Importer Stock Number with the 
currently selected vehicle.

If a match is found, the VIN, Year, Make, and Model fields 
will auto-populate with the vehicle’s details.

If no match is found, red text will appear, displaying: “No 
Match Found.”

**VIN:**

A read-only label that displays the VIN of the searched 
vehicle.

By default, the field displays a “-” if no vehicle has been 
found.

**Year:**

A read-only label that displays the Year of the searched 
vehicle.

By default, the field displays a “-” if no vehicle has been 
found.

**Make:**

A read-only label that displays the Make of the searched 
vehicle.

By default, the field displays a “-” if no vehicle has been 
found.

**Model:**

A read-only label that displays the Model of the searched 
vehicle.

By default, the field displays a “-” if no vehicle has been 
found.

**Importer Stock #:**

A read-only label that displays the Importer Stock Number of the vehicle. By default, the field displays a “-” if no vehicle has been found. 

**NEXT Button:**

Clicking the Next Button will take the user to the next step in the Damage process. 

`}
                  </GreenText>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </form>
      )}
    </Formik>
  )
}
